import React, { useState, useMemo } from 'react'
import {
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type Table as TableType,
  type Row,
  type Column,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Search,
  X,
  Download,
  Filter,
  Settings2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'

export interface DataTableColumn<TData> {
  id?: string
  accessorKey: string
  header: string | ((props: { column: Column<TData, unknown> }) => React.ReactNode)
  cell?: (props: { row: Row<TData>; getValue: () => unknown }) => React.ReactNode
  enableSorting?: boolean
  enableHiding?: boolean
  filterFn?: 'text' | 'number' | 'date' | 'select' | 'boolean'
  filterOptions?: { label: string; value: string }[]
  meta?: {
    headerClassName?: string
    cellClassName?: string
  }
}

export interface DataTableFilter {
  column: string
  type: 'text' | 'select' | 'date' | 'number' | 'boolean'
  label: string
  placeholder?: string
  options?: { label: string; value: string }[]
}

export interface DataTableAdvancedProps<TData> {
  columns: DataTableColumn<TData>[]
  data: TData[]
  searchable?: boolean
  searchPlaceholder?: string
  searchKeys?: string[]
  filters?: DataTableFilter[]
  showColumnVisibility?: boolean
  showPagination?: boolean
  showSelection?: boolean
  pageSize?: number
  pageSizeOptions?: number[]
  onRowSelect?: (rows: TData[]) => void
  onRowClick?: (row: TData) => void
  onExport?: (data: TData[]) => void
  exportFileName?: string
  actions?: (row: TData) => React.ReactNode
  emptyMessage?: string
  loading?: boolean
  stickyHeader?: boolean
  className?: string
  toolbar?: React.ReactNode
  showRowNumbers?: boolean
  // Server-side pagination props
  serverSidePagination?: boolean
  totalRows?: number
  currentPage?: number
  onPageChange?: (page: number) => void
  onPageSizeChange?: (size: number) => void
}

function DataTableToolbar<TData>({
  table,
  searchable,
  searchPlaceholder,
  filters,
  showColumnVisibility,
  onExport,
  toolbar,
}: {
  table: TableType<TData>
  searchable?: boolean
  searchPlaceholder?: string
  filters?: DataTableFilter[]
  showColumnVisibility?: boolean
  onExport?: (data: TData[]) => void
  toolbar?: React.ReactNode
}) {
  const [showFilters, setShowFilters] = useState(false)
  const isFiltered = table.getState().columnFilters.length > 0

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-1 items-center space-x-2">
          {searchable && (
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={searchPlaceholder || 'Search...'}
                value={(table.getColumn('_search')?.getFilterValue() as string) ?? ''}
                onChange={(event) =>
                  table.getColumn('_search')?.setFilterValue(event.target.value)
                }
                className="pl-8"
              />
            </div>
          )}
          
          {filters && filters.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={cn(isFiltered && 'border-primary')}
            >
              <Filter className="mr-2 h-4 w-4" />
              Filters
              {isFiltered && (
                <Badge variant="secondary" className="ml-2 rounded-sm px-1">
                  {table.getState().columnFilters.length}
                </Badge>
              )}
            </Button>
          )}

          {isFiltered && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => table.resetColumnFilters()}
              className="h-8 px-2 lg:px-3"
            >
              Reset
              <X className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {toolbar}
          
          {onExport && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onExport(table.getFilteredRowModel().rows.map(row => row.original))}
            >
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          )}

          {showColumnVisibility && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings2 className="mr-2 h-4 w-4" />
                  View
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[200px]">
                <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {table
                  .getAllColumns()
                  .filter(
                    (column) =>
                      typeof column.accessorFn !== 'undefined' && column.getCanHide()
                  )
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) => column.toggleVisibility(!!value)}
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    )
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {showFilters && filters && (
        <div className="rounded-lg border p-4">
          <div className="mb-2 text-sm font-medium">Filters</div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filters.map((filter) => (
              <div key={filter.column} className="space-y-2">
                <label className="text-sm font-medium">{filter.label}</label>
                {filter.type === 'text' && (
                  <Input
                    placeholder={filter.placeholder || `Filter ${filter.label}...`}
                    value={(table.getColumn(filter.column)?.getFilterValue() as string) ?? ''}
                    onChange={(event) =>
                      table.getColumn(filter.column)?.setFilterValue(event.target.value)
                    }
                    className="h-8"
                  />
                )}
                {filter.type === 'select' && (
                  <Select
                    value={(table.getColumn(filter.column)?.getFilterValue() as string) ?? ''}
                    onValueChange={(value) =>
                      table.getColumn(filter.column)?.setFilterValue(value === 'all' ? '' : value)
                    }
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      {filter.options?.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {filter.type === 'number' && (
                  <div className="flex space-x-2">
                    <Input
                      type="number"
                      placeholder="Min"
                      value={(table.getColumn(filter.column)?.getFilterValue() as [number, number])?.[0] ?? ''}
                      onChange={(event) => {
                        const current = table.getColumn(filter.column)?.getFilterValue() as [number, number]
                        table.getColumn(filter.column)?.setFilterValue([
                          event.target.value ? Number(event.target.value) : undefined,
                          current?.[1],
                        ])
                      }}
                      className="h-8"
                    />
                    <Input
                      type="number"
                      placeholder="Max"
                      value={(table.getColumn(filter.column)?.getFilterValue() as [number, number])?.[1] ?? ''}
                      onChange={(event) => {
                        const current = table.getColumn(filter.column)?.getFilterValue() as [number, number]
                        table.getColumn(filter.column)?.setFilterValue([
                          current?.[0],
                          event.target.value ? Number(event.target.value) : undefined,
                        ])
                      }}
                      className="h-8"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function DataTablePagination<TData>({
  table,
  pageSizeOptions = [10, 20, 30, 40, 50],
  serverSidePagination = false,
  totalRows = 0,
  currentPage = 1,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
}: {
  table: TableType<TData>
  pageSizeOptions?: number[]
  serverSidePagination?: boolean
  totalRows?: number
  currentPage?: number
  pageSize?: number
  onPageChange?: (page: number) => void
  onPageSizeChange?: (size: number) => void
}) {
  const totalPages = serverSidePagination 
    ? Math.ceil(totalRows / pageSize)
    : table.getPageCount()

  const currentPageIndex = serverSidePagination
    ? currentPage - 1
    : table.getState().pagination.pageIndex

  const currentPageSize = serverSidePagination
    ? pageSize
    : table.getState().pagination.pageSize

  return (
    <div className="flex items-center justify-between px-2">
      <div className="flex-1 text-sm text-muted-foreground">
        {serverSidePagination ? (
          `Showing ${Math.min((currentPage - 1) * pageSize + 1, totalRows)} to ${Math.min(currentPage * pageSize, totalRows)} of ${totalRows} row(s)`
        ) : (
          `${table.getFilteredSelectedRowModel().rows.length} of ${table.getFilteredRowModel().rows.length} row(s) selected.`
        )}
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Rows per page</p>
          <Select
            value={`${currentPageSize}`}
            onValueChange={(value) => {
              if (serverSidePagination && onPageSizeChange) {
                onPageSizeChange(Number(value))
              } else {
                table.setPageSize(Number(value))
              }
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={currentPageSize} />
            </SelectTrigger>
            <SelectContent side="top">
              {pageSizeOptions.map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
          Page {currentPageIndex + 1} of {totalPages}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => {
              if (serverSidePagination && onPageChange) {
                onPageChange(1)
              } else {
                table.setPageIndex(0)
              }
            }}
            disabled={serverSidePagination ? currentPage <= 1 : !table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to first page</span>
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => {
              if (serverSidePagination && onPageChange) {
                onPageChange(currentPage - 1)
              } else {
                table.previousPage()
              }
            }}
            disabled={serverSidePagination ? currentPage <= 1 : !table.getCanPreviousPage()}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={() => {
              if (serverSidePagination && onPageChange) {
                onPageChange(currentPage + 1)
              } else {
                table.nextPage()
              }
            }}
            disabled={serverSidePagination ? currentPage >= totalPages : !table.getCanNextPage()}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={() => {
              if (serverSidePagination && onPageChange) {
                onPageChange(totalPages)
              } else {
                table.setPageIndex(table.getPageCount() - 1)
              }
            }}
            disabled={serverSidePagination ? currentPage >= totalPages : !table.getCanNextPage()}
          >
            <span className="sr-only">Go to last page</span>
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

export function DataTableAdvanced<TData>({
  columns,
  data,
  searchable = true,
  searchPlaceholder,
  searchKeys = [],
  filters = [],
  showColumnVisibility = true,
  showPagination = true,
  showSelection = false,
  pageSize = 10,
  pageSizeOptions = [10, 20, 30, 40, 50],
  onRowSelect,
  onRowClick,
  onExport,
  actions,
  emptyMessage = 'No results.',
  loading = false,
  stickyHeader = false,
  className,
  toolbar,
  showRowNumbers = false,
  serverSidePagination = false,
  totalRows = 0,
  currentPage = 1,
  onPageChange,
  onPageSizeChange,
}: DataTableAdvancedProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState({})
  const [globalFilter, setGlobalFilter] = useState('')

  // Convert columns to react-table format
  const tableColumns = useMemo<ColumnDef<TData>[]>(() => {
    const cols: ColumnDef<TData>[] = []

    // Add selection column if enabled
    if (showSelection) {
      cols.push({
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        enableHiding: false,
      })
    }

    // Add row number column if enabled
    if (showRowNumbers) {
      cols.push({
        id: 'rowNumber',
        header: '#',
        cell: ({ row }) => (
          <div className="text-muted-foreground">{row.index + 1}</div>
        ),
        enableSorting: false,
        enableHiding: false,
      })
    }

    // Add data columns
    columns.forEach((column) => {
      cols.push({
        id: column.id || column.accessorKey,
        accessorKey: column.accessorKey,
        header: typeof column.header === 'string'
          ? ({ column: col }) => {
              return column.enableSorting !== false ? (
                <Button
                  variant="ghost"
                  onClick={() => col.toggleSorting(col.getIsSorted() === 'asc')}
                  className="-ml-3 h-8 data-[state=open]:bg-accent"
                >
                  {column.header as string}
                  {col.getIsSorted() === 'asc' ? (
                    <ArrowUp className="ml-2 h-4 w-4" />
                  ) : col.getIsSorted() === 'desc' ? (
                    <ArrowDown className="ml-2 h-4 w-4" />
                  ) : (
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                  )}
                </Button>
              ) : (
                column.header as string
              )
            }
          : column.header,
        cell: column.cell || (({ getValue }) => getValue()),
        enableSorting: column.enableSorting !== false,
        enableHiding: column.enableHiding !== false,
        meta: column.meta,
      })
    })

    // Add actions column if provided
    if (actions) {
      cols.push({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => actions(row.original),
        enableSorting: false,
        enableHiding: false,
      })
    }

    // Add a hidden column for global search
    if (searchable && searchKeys.length > 0) {
      cols.push({
        id: '_search',
        accessorFn: (row: TData) => {
          return searchKeys.map(key => (row as any)[key]).join(' ')
        },
        header: () => null,
        cell: () => null,
        enableHiding: false,
        enableSorting: false,
      })
    }

    return cols
  }, [columns, showSelection, showRowNumbers, actions, searchable, searchKeys])

  const table = useReactTable({
    data,
    columns: tableColumns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: (showPagination && !serverSidePagination) ? getPaginationRowModel() : undefined,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: 'includesString',
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize,
      },
    },
    manualPagination: serverSidePagination,
    pageCount: serverSidePagination ? Math.ceil(totalRows / pageSize) : undefined,
  })

  // Handle row selection callback
  React.useEffect(() => {
    if (onRowSelect) {
      const selectedRows = table.getFilteredSelectedRowModel().rows.map(row => row.original)
      onRowSelect(selectedRows)
    }
  }, [rowSelection, onRowSelect, table])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('space-y-4', className)}>
      <DataTableToolbar
        table={table}
        searchable={searchable}
        searchPlaceholder={searchPlaceholder}
        filters={filters}
        showColumnVisibility={showColumnVisibility}
        onExport={onExport}
        toolbar={toolbar}
      />
      
      <div className="rounded-md border">
        <Table>
          <TableHeader className={cn(stickyHeader && 'sticky top-0 bg-background z-10')}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        (header.column.columnDef.meta as { headerClassName?: string })?.headerClassName
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={() => onRowClick?.(row.original)}
                  className={cn(onRowClick && 'cursor-pointer hover:bg-muted/50')}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        (cell.column.columnDef.meta as { cellClassName?: string })?.cellClassName
                      )}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={tableColumns.length}
                  className="h-24 text-center"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      
      {showPagination && (
        <DataTablePagination 
          table={table} 
          pageSizeOptions={pageSizeOptions}
          serverSidePagination={serverSidePagination}
          totalRows={totalRows}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  )
}